/**
 * File Viewer Modal — image zoom controls & PDF resize fix.
 *
 * Shared between drawing.js (drawing page) and drawing_viewer_common.js
 * (other pages that use the reusable viewer).  Call initFileViewerModal()
 * immediately after inserting the view_file.html modal into the DOM.
 */

/**
 * Initialise zoom controls (images) and PDF resize fix after the
 * file-viewer modal is injected into the DOM.
 */
export function initFileViewerModal() {
  const modal = document.getElementById("file-viewer-modal");
  if (!modal) return;

  // ── Image zoom ──────────────────────────────────────────────
  const img = modal.querySelector("#viewer-image");
  const wrapper = modal.querySelector("#viewer-image-wrapper");

  if (img && wrapper) {
    img.addEventListener("error", () => {
      wrapper.innerHTML = '<p class="text-red-500">Failed to load image</p>';
    });

    let currentScale = null; // null = fit mode
    const MIN_SCALE = 0.1;
    const MAX_SCALE = 5;
    const STEP = 0.25;

    function getFitScale() {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return 1;
      const cw = wrapper.clientWidth;
      const ch = window.innerHeight * 0.7;
      return Math.min(cw / nw, ch / nh, 1);
    }

    function applyFit() {
      currentScale = null;
      img.style.width = "";
      img.style.height = "";
      img.className = "max-w-full max-h-[70vh] object-contain shadow-lg";
      wrapper.className = "flex items-center justify-center min-h-[460px]";
      updateLabel();
    }

    function applyZoom(scale) {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return; // image not loaded yet
      currentScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
      img.className = "shadow-lg";
      img.style.width = Math.round(nw * currentScale) + "px";
      img.style.height = Math.round(nh * currentScale) + "px";
      wrapper.className = "min-h-[460px]";
      updateLabel();
    }

    function updateLabel() {
      const el = modal.querySelector("#viewer-zoom-level");
      if (!el) return;
      if (currentScale === null) {
        el.textContent = "Fit";
      } else {
        el.textContent = Math.round(currentScale * 100) + "%";
      }
    }

    function getEffectiveScale() {
      return currentScale !== null ? currentScale : getFitScale();
    }

    modal
      .querySelector("#viewer-zoom-in")
      ?.addEventListener("click", () => {
        applyZoom(getEffectiveScale() + STEP);
      });

    modal
      .querySelector("#viewer-zoom-out")
      ?.addEventListener("click", () => {
        applyZoom(getEffectiveScale() - STEP);
      });

    modal
      .querySelector("#viewer-zoom-fit")
      ?.addEventListener("click", applyFit);

    modal
      .querySelector("#viewer-zoom-actual")
      ?.addEventListener("click", () => {
        applyZoom(1);
      });

    // Ctrl/Cmd + scroll-wheel zoom
    const scrollTarget = modal.querySelector(".overflow-auto");
    if (scrollTarget) {
      scrollTarget.addEventListener(
        "wheel",
        (e) => {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY < 0 ? STEP : -STEP;
            applyZoom(getEffectiveScale() + delta);
          }
        },
        { passive: false },
      );
    }
  }

  // ── PDF resize fix ──────────────────────────────────────────
  // Chrome's embedded PDF viewer switches to a compact/mobile mode
  // when browser zoom changes the effective viewport size, and does
  // not revert when zoom is reset.  Detect zoom changes via
  // devicePixelRatio and recreate the <iframe> to force re-render.
  const pdfContainer = modal.querySelector("#viewer-pdf-container");
  if (pdfContainer) {
    let lastDpr = window.devicePixelRatio;
    let debounceTimer;

    function onResize() {
      const dpr = window.devicePixelRatio;
      if (dpr === lastDpr) return;
      lastDpr = dpr;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (!document.getElementById("file-viewer-modal")) return;
        const src = pdfContainer.dataset.pdfSrc;
        const old = pdfContainer.querySelector("iframe");
        if (!old || !src) return;
        const fresh = document.createElement("iframe");
        fresh.id = "viewer-pdf-embed";
        fresh.src = src;
        fresh.className = "w-full h-full border-0";
        fresh.title = modal.querySelector("h3")?.textContent || "";
        pdfContainer.replaceChild(fresh, old);
      }, 300);
    }

    window.addEventListener("resize", onResize);

    // Clean up listener when the modal is removed from DOM
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.removedNodes) {
          if (node === modal) {
            window.removeEventListener("resize", onResize);
            obs.disconnect();
            return;
          }
        }
      }
    });
    if (modal.parentNode) {
      obs.observe(modal.parentNode, { childList: true });
    }
  }
}
